import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import PolicyModal from "../client/PolicyModal";
import DatePicker from "../DatePicker";
import * as Yup from "yup";
import { capitalizeWords } from "../../utils/helps";
import BusinessFieldSelection from "../BusinessFieldSelection";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import ReCAPTCHA from "react-google-recaptcha";
import ReferrerSelect from "../ReferrerParnerSelection";

const RECAPTCHA_WIDTH = 304;
function getRecaptchaScale(parentWidth) {
  if (!parentWidth) return 1;
  // if (parentWidth >= RECAPTCHA_WIDTH) return 1;
  return parentWidth / RECAPTCHA_WIDTH;
}

const partnerSchema = Yup.object().shape({
  companyName: Yup.string().trim().required("Tên đơn vị không được để trống"),
  userEmail: Yup.string()
    .trim()
    .email("Email không đúng định dạng")
    .required("Email không được để trống"),
  userPhone: Yup.string()
    .trim()
    .matches(
      /^(0[3|5|7|8|9])+([0-9]{8})\b/,
      "Số điện thoại không đúng định dạng",
    )
    .required("Số điện thoại không được để trống"),
  businessFieldIds: Yup.array()
    .of(Yup.object())
    .min(1, "Phải chọn ít nhất 1 lĩnh vực")
    .required("Phải chọn ít nhất 1 lĩnh vực"),
  businessAddress: Yup.string()
    .trim()
    .required("Địa chỉ đơn vị không được để trống"),
  websiteOrHotline: Yup.string(),
  hotline: Yup.string().matches(/^\d*$/, "Hotline không hợp lệ"),
  position: Yup.string().required("Chức vụ tại đơn vị không được để trống"),
  taxCode: Yup.string().nullable().matches(/^\d*$/, "Mã số thuế không hợp lệ"),
});

const userSchema = Yup.object().shape({
  userFullName: Yup.string().trim().required("Họ tên không được để trống"),
  placeOfBirth: Yup.string().trim(),
  dateOfBirth: Yup.date()
    .typeError("Ngày sinh không hợp lệ")
    .required("Ngày sinh không được để trống"),
  idCard: Yup.string().trim().matches(/^\d*$/, "Số căn cước không hợp lệ"),
});

const imageSchema = Yup.object().shape({
  logo: Yup.mixed().nullable(),
  avatar: Yup.mixed().required("Ảnh đại diện không được để trống"),
});

const MemberRegistrationForm = ({ useId = false }) => {
  const { loading } = useSelector((state) => state.partners);
  const [partnerFormData, setpartnerFormData] = useState({
    userEmail: "",
    userPhone: "",
    companyName: "",
    businessAddress: "",
    taxCode: "",
    websiteOrHotline: "",
    hotline: "",
    businessField: "",
    businessFieldIds: [],
    position: "",
  });

  const [userFormData, setUserFormData] = useState({
    userFullName: "",
    placeOfBirth: "",
    dateOfBirth: "",
    idCard: "",
  });

  const [imageFormData, setimageFormData] = useState({
    logo: null,
    avatar: null,
  });

  const partnerFieldLabels = {
    userEmail: "Email",
    userPhone: "Số điện thoại",
    companyName: "Tên đơn vị",
    businessFieldIds: "Lĩnh vực kinh doanh",
    businessAddress: "Địa chỉ đơn vị",
    taxCode: "Mã số thuế",
    websiteOrHotline: "Website",
    hotline: "Hotline",
    position: "Chức vụ tại đơn vị",
  };

  const userFieldLabels = {
    userFullName: "Họ và tên",
    placeOfBirth: "Nơi sinh",
    dateOfBirth: "Ngày sinh",
    idCard: "Số căn cước",
  };

  const partnerFieldTypes = {
    userEmail: "email",
    userPhone: "text",
    companyName: "text",
    businessAddress: "text",
    websiteOrHotline: "text",
    hotline: "text",
  };

  const userFieldTypes = {
    userFullName: "text",
    placeOfBirth: "text",
    dateOfBirth: "date",
    idCard: "text",
  };

  const [company, setCompany] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedBusinessFields, setSelectedBusinessFields] = useState([]);
  const [logoPreviewImage, setLogoPreviewImage] = useState(null);
  const [avatarPreviewImage, setAvatarPreviewImage] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const recaptchaWrapperRef = useRef(null);
  const [recaptchaScale, setRecaptchaScale] = useState(1);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    function updateScale() {
      if (recaptchaWrapperRef.current) {
        const width = recaptchaWrapperRef.current.offsetWidth;
        setRecaptchaScale(getRecaptchaScale(width));
      }
    }
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  const handleCloseModal = (value) => {
    setIsOpenModal(false);
    if (value) setAgreedToTerms(true);
  };

  useEffect(() => {
    getCompany();
  }, []);
  const getCompany = () => {
    ApiCustom.get(`categories/public?t=company`)
      .then((res) => setCompany(res.data?.data?.data))
      .catch((err) => console.log(err));
  };

  const RequiredLabel = ({ text }) => (
    <div className="text-sm mb-1">
      {text} <span className="text-red-500">*</span>
    </div>
  );

  const handleBusinessFieldSelectorChange = async (selectedBusinessField) => {
    try {
      setErrors((prev) => ({ ...prev, ["businessFieldIds"]: undefined }));
      setSelectedBusinessFields(selectedBusinessField);
      setpartnerFormData((prev) => ({
        ...prev,
        businessFieldIds: selectedBusinessField,
      }));
      await Yup.reach(partnerSchema, "businessFieldIds").validate(
        selectedBusinessField,
      );
    } catch (validationError) {
      setErrors((prev) => ({
        ...prev,
        ["businessFieldIds"]: validationError.message,
      }));
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    let newValue = value;

    if (name === "logo" || (name === "avatar" && files[0])) {
      const file = files[0];
      const maxSize = 2 * 1024 * 1024;
      if (file?.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          [name]: "Hình ảnh phải có kích thước nhỏ hơn hoặc bằng 2Mb",
        }));
        return;
      }
      if (file) {
        newValue = file;
        const reader = new FileReader();
        reader.onloadend = () => {
          if (name === "logo") {
            setLogoPreviewImage(reader.result);
            setimageFormData((prev) => ({
              ...prev,
              logo: file,
            }));
          } else if (name === "avatar") {
            setAvatarPreviewImage(reader.result);
            setimageFormData((prev) => ({
              ...prev,
              avatar: file,
            }));
          }
        };
        reader.readAsDataURL(file);
      }
    }

    try {
      if (Object.keys(partnerFormData).includes(name)) {
        setpartnerFormData((prev) => ({ ...prev, [name]: newValue }));
        await Yup.reach(partnerSchema, name).validate(newValue);
      } else if (Object.keys(userFormData).includes(name)) {
        setUserFormData((prev) => ({ ...prev, [name]: newValue }));
        await Yup.reach(userSchema, name).validate(newValue);
      }
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (validationError) {
      setErrors((prev) => ({ ...prev, [name]: validationError.message }));
    }
  };

  const handleTermsChange = () => {
    if (agreedToTerms) {
      setAgreedToTerms(false);
      return;
    }
    setIsOpenModal(true);
    setErrors((prev) => ({
      ...prev,
      terms: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      toast.error("Vui lòng xác nhận reCAPTCHA!");
      return;
    }

    try {
      const formData = {
        ...partnerFormData,
        ...userFormData,
        ...imageFormData,
      };
      const schema = partnerSchema.concat(userSchema).concat(imageSchema);
      await schema.validate(formData, { abortEarly: false });
      formData["userFullName"] = capitalizeWords(formData["userFullName"]);
      formData["companyName"] = formData["companyName"].toUpperCase();
      formData.businessFieldIds = formData.businessFieldIds.map((e) =>
        parseInt(e.value),
      );
      const { logo, avatar, ...dataToRegis } = formData;
      dataToRegis["recaptchaCode"] = captchaToken;
      dataToRegis["referrerId"] = referrer?.company?.id || null;
      ApiCustom.post(`/company-register`, dataToRegis)
        .then(async (res) => {
          const imageFormData = new FormData();
          imageFormData.append("files[]", avatar);
          imageFormData.append("files[]", logo);
          ApiCustom.put(
            `/company-update-image/${res?.data?.data?.id}`,
            imageFormData,
          )
            .then(async (res) => {
              if (res.status === 200) {
                console.log(res.status);
              }
            })
            .catch((error) => {
              console.error("Error updating images:", error);
            });

          setpartnerFormData({
            userEmail: "",
            userPhone: "",
            companyName: "",
            businessAddress: "",
            taxCode: "",
            businessFieldIds: [],
            websiteOrHotline: "",
            hotline: "",
            businessField: "",
            position: "",
          });
          setUserFormData({
            userFullName: "",
            placeOfBirth: "",
            dateOfBirth: "",
            idCard: "",
          });
          setimageFormData({
            logo: null,
            avatar: null,
          });
          setReferrer(null);
          setLogoPreviewImage(null);
          setAvatarPreviewImage(null);

          setSelectedBusinessFields([]);
          toast.success("Đăng ký thành công!");
          recaptchaRef.current.reset();
          setAgreedToTerms(false);
        })
        .catch((error) => {
          if (error?.status == 422) {
            setErrors((prev) => ({
              ...prev,
              ...error?.response?.data?.messages,
            }));
          } else if (error?.status == 400) {
            toast.error(error?.response?.data?.messages);
          } else {
            toast.error("Đã xảy ra lỗi!");
          }
        });
    } catch (error) {
      if (error.inner) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi!");
      }
    }
  };

  return (
    <div
      className="flex justify-center items-center"
      id={useId ? "dang-ky" : ""}
    >
      <div className="w-full max-w-md bg-base-100 rounded-lg py-2">
        <div className="space-y-4">
          {["userFullName"].map((key) => (
            <div className="form-control" key={key}>
              <RequiredLabel text={userFieldLabels[key]} />
              <input
                style={{ textTransform: "capitalize" }}
                type={userFieldTypes[key]}
                name={key}
                value={userFormData[key]}
                onChange={handleChange}
                className={`input input-bordered w-full ${
                  errors[key] ? "input-error" : ""
                }`}
                required
              />
              {errors[key] && (
                <p className="text-red-500 text-sm">{errors[key]}</p>
              )}
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["idCard"].map((key) => (
              <div className="form-control" key={key}>
                {["idCard"].includes(key) ? (
                  <div className="text-sm mb-1">{userFieldLabels[key]}</div>
                ) : (
                  <RequiredLabel text={userFieldLabels[key]} />
                )}
                <input
                  type={userFieldTypes[key]}
                  name={key}
                  value={userFormData[key]}
                  onChange={handleChange}
                  className={`input max-h-11 input-bordered w-full ${
                    errors[key] ? "input-error" : ""
                  }`}
                  required
                />
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
            <div>
              <RequiredLabel text={userFieldLabels["dateOfBirth"]} />
              <DatePicker
                name="dateOfBirth"
                value={userFormData["dateOfBirth"]}
                onChange={(name, value) => {
                  handleChange({
                    target: {
                      name,
                      value,
                    },
                  });
                }}
                error={errors["dateOfBirth"]}
              />
              {errors["dateOfBirth"] && (
                <p className="text-red-500 text-sm">{errors["dateOfBirth"]}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["placeOfBirth"].map((key) => (
              <div className="form-control" key={key}>
                {["placeOfBirth"].includes(key) ? (
                  <div className="text-sm mb-1">{userFieldLabels[key]}</div>
                ) : (
                  <RequiredLabel text={userFieldLabels[key]} />
                )}
                <input
                  type={userFieldTypes[key]}
                  name={key}
                  value={userFormData[key]}
                  onChange={handleChange}
                  className={`input input-bordered w-full ${
                    errors[key] ? "input-error" : ""
                  }`}
                  required
                />
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
            {["userPhone"].map((key) => (
              <div className="form-control" key={key}>
                <RequiredLabel text={partnerFieldLabels[key]} />
                <input
                  type={partnerFieldTypes[key]}
                  name={key}
                  value={partnerFormData[key]}
                  onChange={handleChange}
                  className={`input input-bordered w-full ${
                    errors[key] ? "input-error" : ""
                  }`}
                  required
                />
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
          </div>
          {["userEmail"].map((key) => (
            <div className="form-control" key={key}>
              <div className="text-sm mb-1">
                {partnerFieldLabels[key]}{" "}
                <span className="text-red-500">* </span>
                <span className="italic">
                  (Vui lòng cung cấp email chính xác)
                </span>
              </div>
              <input
                type={partnerFieldTypes[key]}
                name={key}
                value={partnerFormData[key]}
                onChange={handleChange}
                className={`input input-bordered w-full ${
                  errors[key] ? "input-error" : ""
                }`}
                required
              />
              {errors[key] && (
                <p className="text-red-500 text-sm">{errors[key]}</p>
              )}
            </div>
          ))}
          {["companyName"].map((key) => (
            <div className="form-control" key={key}>
              <RequiredLabel text={partnerFieldLabels[key]} />
              <input
                style={{ textTransform: "uppercase" }}
                type={partnerFieldTypes[key]}
                name={key}
                value={partnerFormData[key]}
                onChange={handleChange}
                className={`input input-bordered w-full ${
                  errors[key] ? "input-error" : ""
                }`}
                required
              />
              {["userEmail"].includes(key) && (
                <div className="mb-2 text-[14px] mt-2">
                  <span className="text-red-500 mr-2">*</span>Vui lòng cung cấp
                  email chính xác
                </div>
              )}
              {errors[key] && (
                <p className="text-red-500 text-sm">{errors[key]}</p>
              )}
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["taxCode"].map((key) => (
              <div className="form-control" key={key}>
                <div className="text-sm mb-1">{partnerFieldLabels[key]}</div>
                <input
                  type={partnerFieldTypes[key]}
                  name={key}
                  value={partnerFormData[key]}
                  onChange={handleChange}
                  className={`input input-bordered w-full ${
                    errors[key] ? "input-error" : ""
                  }`}
                  required
                />
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
            {["position"].map((key) => (
              <div className="form-control" key={key}>
                <RequiredLabel text={partnerFieldLabels[key]} />
                <input
                  type={partnerFieldTypes[key]}
                  name={key}
                  value={partnerFormData[key]}
                  onChange={handleChange}
                  className={`input input-bordered w-full ${
                    errors[key] ? "input-error" : ""
                  }`}
                  required
                />
                {["userEmail"].includes(key) && (
                  <div className="mb-2 text-[14px] mt-2">
                    <span className="text-red-500 mr-2">*</span>Vui lòng cung
                    cấp email chính xác
                  </div>
                )}
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
          </div>
          {["businessAddress"].map((key) => (
            <div className="form-control" key={key}>
              {[].includes(key) ? (
                <div className="text-sm mb-1">{partnerFieldLabels[key]}</div>
              ) : (
                <RequiredLabel text={partnerFieldLabels[key]} />
              )}
              <input
                type={partnerFieldTypes[key]}
                name={key}
                value={partnerFormData[key]}
                onChange={handleChange}
                className={`input input-bordered w-full ${
                  errors[key] ? "input-error" : ""
                }`}
                required
              />
              {errors[key] && (
                <p className="text-red-500 text-sm">{errors[key]}</p>
              )}
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["websiteOrHotline", "hotline"].map((key) => (
              <div className="form-control" key={key}>
                {["websiteOrHotline", "hotline"].includes(key) ? (
                  <div className="text-sm mb-1">{partnerFieldLabels[key]}</div>
                ) : (
                  <RequiredLabel text={partnerFieldLabels[key]} />
                )}
                <input
                  type={partnerFieldTypes[key]}
                  name={key}
                  value={partnerFormData[key]}
                  onChange={handleChange}
                  className={`input input-bordered w-full ${
                    errors[key] ? "input-error" : ""
                  }`}
                  required
                />
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
          </div>
          <div>
            <BusinessFieldSelection
              options={company.map((e) => ({ value: e.id, label: e.name }))}
              onChange={handleBusinessFieldSelectorChange}
              error={errors.businessFieldIds != null}
              selectedFields={selectedBusinessFields}
            />
            {errors.businessFieldIds && (
              <span className="text-red-500 text-sm">
                {errors.businessFieldIds}
              </span>
            )}
          </div>

          <div>
            <ReferrerSelect
              value={referrer}
              onChange={setReferrer}
              error={errors.referrer != null}
            />
            {errors.referrer && (
              <span className="text-red-500 text-sm">{errors.referrer}</span>
            )}
          </div>

          <div className="flex justify-around">
            <div className="flex-1 max-w-28">
              <label className="block text-sm mb-1">
                Ảnh đại diện
                <span className="text-red-500"> *</span>
              </label>
              <div className="mt-1">
                <label>
                  <div className="relative w-full aspect-square border-2 border-gray-200 border-dashed rounded-lg overflow-hidden">
                    {avatarPreviewImage ? (
                      <img
                        src={avatarPreviewImage}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <img
                          src="/avatar_placeholder2.png"
                          alt="Avatar placeholder"
                          className="opacity-50 object-contain scale-[1.15]"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      name="avatar"
                      onChange={handleChange}
                      className="hidden"
                      id="avatar-image-upload"
                      accept="image/*"
                    />
                    <label
                      htmlFor="avatar-image-upload"
                      className="absolute bottom-1 right-1 cursor-pointer rounded-md"
                    >
                      <PencilSquareIcon
                        className={`h-4 w-4 ${
                          avatarPreviewImage ? "text-white" : "text-black"
                        }`}
                      />
                    </label>
                  </div>
                </label>
              </div>
            </div>
            <div className=" flex-1 max-w-28">
              <label className="block text-sm mb-1">Logo đơn vị</label>
              <div className="mt-1">
                <label>
                  <div className="relative max-w-28 w-full aspect-square border-2 border-gray-200 border-dashed rounded-lg overflow-hidden">
                    {logoPreviewImage ? (
                      <img
                        src={logoPreviewImage}
                        alt="Preview"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <img
                          src="/logo_placeholder2.png"
                          alt="Logo placeholder"
                          className="opacity-50 object-contain"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      name="logo"
                      onChange={handleChange}
                      className="hidden"
                      id="logo-image-upload"
                      accept="image/*"
                    />
                    <label
                      htmlFor="logo-image-upload"
                      className="absolute bottom-1 right-1 cursor-pointer rounded-md"
                    >
                      <PencilSquareIcon
                        className={`h-4 w-4 ${
                          logoPreviewImage ? "text-white" : "text-black"
                        }`}
                      />
                    </label>
                  </div>
                </label>
              </div>
            </div>
          </div>
          {errors["avatar"] && (
            <p className="mt-1 text-sm text-red-600">{errors["avatar"]}</p>
          )}

          <div className="form-control">
            <label className="label cursor-pointer justify-start">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={handleTermsChange}
                className={`checkbox ${errors.terms ? "checkbox-error" : ""}`}
              />
              <span className="label-text ml-2">
                Tôi đồng ý với điều khoản của hội
                <span className="text-red-500 ml-1">*</span>
              </span>
            </label>
            {errors.terms && (
              <p className="text-red-500 text-sm">{errors.terms}</p>
            )}
          </div>
          <div ref={recaptchaWrapperRef} className="w-full flex justify-center">
            <div
              className="origin-center"
              style={{
                transform: `scaleX(${recaptchaScale})`,
                width: `${RECAPTCHA_WIDTH}px`,
                height: `78px`,
              }}
            >
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </div>
          </div>
          <div className="form-control mt-6">
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn btn-warning w-full btn-sm min-h-12"
              disabled={loading || !agreedToTerms || !captchaToken}
            >
              {loading ? "Đang gửi..." : "Gửi thông tin"}
            </button>
          </div>
        </div>
      </div>
      <PolicyModal isOpen={isOpenModal} onClose={handleCloseModal} />
    </div>
  );
};

export default MemberRegistrationForm;
